/**
 * Acciones rápidas del panel de Inicio (réplica del "Acciones Rápidas" del SSR).
 *
 * NO reimplementa permisos ni navegación: deriva las acciones de
 * `useNavSections()`, que ya filtró las entradas del menú por permiso/rol/flags
 * de sede contra la sesión (`docs/frontend_architecture.md`, Pr. 5). Aplana las
 * secciones, las indexa por `id` y selecciona el subconjunto de accesos directos
 * en el ORDEN del panel del SSR. Un acceso que no esté visible para la sesión
 * (p. ej. un operario sin "Nueva orden") simplemente no aparece.
 */
import { useMemo } from "react";

import { useNavSections, type NavItem } from "@/features/navigation";

/**
 * Ids de las acciones rápidas, en el ORDEN del panel del SSR (Cotizador,
 * Optimizador, Nueva orden, Horario semanal, Horario láser, Pedidos de láminas,
 * Inventario, Talonarios). Son ids REALES del `navConfig` (ojo: el horario
 * semanal es `schedule-week`, no `schedule`).
 */
export const QUICK_ACTION_IDS = [
  "cotizador",
  "cutlist",
  "orders-new",
  "schedule-week",
  "laser-schedule",
  "pedidos-laminas",
  "inventario",
  "talonarios",
] as const;

/** Acceso directo destacado (azul sólido en el SSR): "Nueva orden". */
export const FEATURED_QUICK_ACTION_ID = "orders-new";

/**
 * Devuelve las acciones rápidas visibles para la sesión, en el orden del panel.
 * Cada `NavItem` ya trae `label` y `to`, así que el botón se pinta y navega sin
 * más lógica.
 */
export function useQuickActions(): NavItem[] {
  const sections = useNavSections();

  return useMemo(() => {
    const byId = new Map(
      sections.flatMap((section) => section.items).map((item) => [item.id, item]),
    );
    return QUICK_ACTION_IDS.map((id) => byId.get(id)).filter(
      (item): item is NavItem => item !== undefined,
    );
  }, [sections]);
}
